import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import CampaignAssignService from "../../../services/OrderingServices/CampaignAssignService";
import AdsService from "../../../services/OrderingServices/AdsService";
import AdvertisersService from "../../../services/OrderingServices/AdvertisersService";
import AdsTagsService from "../../../services/OrderingServices/AdsTagsService";
import DeviceHeartbeatService from "../../../services/DeviceHeartbeatServices/DeviceHeartbeatService";
import DeviceAdsService from "../../../services/OrderingServices/DeviceAdsService";
import CloudRestroUsers from "../../../services/DatabaseService/CloudRestroUsers";
import { debounce } from "lodash"; // npm install lodash
import classnames from "classnames";
import kiosk from "../../../assets/images/kiosk.png";
import CampaignInfoCard from "../components/CampaignInfoCard";
import AddMediaModal from "../components/AddMediaModal";
import CampaignTabsCard from "../components/CampaignTabsCard";

// Custom cache hook for ads data
const useAdsCache = () => {
  const cacheRef = useRef(new Map());
  const cacheTTL = 5 * 60 * 1000; // 5 minutes

  const getCache = (key) => {
    const cached = cacheRef.current.get(key);
    if (cached && Date.now() - cached.timestamp < cacheTTL) {
      return cached.data;
    }
    cacheRef.current.delete(key);
    return null;
  };

  const setCache = (key, data) => {
    cacheRef.current.set(key, {
      data,
      timestamp: Date.now(),
    });
  };

  const clearCache = () => {
    cacheRef.current.clear();
  };

  return { getCache, setCache, clearCache };
};

// Lazy load image hook - Optimized to prevent resets
const useLazyImage = (imageSrc) => {
  const [imageSrcState, setImageSrcState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef(null);
  const loadedSrcRef = useRef(null);

  useEffect(() => {
    // Skip if already loaded this exact src
    if (imageSrcState && loadedSrcRef.current === imageSrc) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          imageSrc &&
          imageSrc !== loadedSrcRef.current
        ) {
          const img = new Image();
          img.onload = () => {
            setImageSrcState(imageSrc);
            loadedSrcRef.current = imageSrc;
            setIsLoading(false);
          };
          img.onerror = () => {
            setIsLoading(false);
          };
          img.src = imageSrc;
        }
      },
      { rootMargin: "50px" },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [imageSrc, imageSrcState]);

  return { imageSrcState, isLoading, imgRef };
};

// Lazy Image Component - Memoized to prevent re-renders
const LazyImage = React.memo(({ src, alt, ...props }) => {
  const { imageSrcState, imgRef } = useLazyImage(src);

  return imageSrcState ? (
    <img
      src={imageSrcState}
      alt={alt}
      {...props}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  ) : (
    <div
      ref={imgRef}
      className="placeholder-glow w-100"
      style={{
        height: props?.style?.height || "180px",
        borderRadius: "4px",
        overflow: "hidden",
      }}
    >
      <div className="placeholder col-12" style={{ height: "100%" }}></div>
    </div>
  );
});

LazyImage.displayName = "LazyImage";

const CampaignDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState(null);
  console.log("🚀 ~ CampaignDetail ~ campaign:", campaign);
  const [selectedAds, setSelectedAds] = useState([]);
  const [unassigning, setUnassigning] = useState(false);

  // Modal state for adding media
  const [modalOpen, setModalOpen] = useState(false);
  const [allAds, setAllAds] = useState([]);
  const [modalSelectedAds, setModalSelectedAds] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalAssigning, setModalAssigning] = useState(false);
  const [mediaType, setMediaType] = useState("");
  const [adFormat, setAdFormat] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Device state
  const [devices, setDevices] = useState([]);
  console.log("🚀 ~ CampaignDetail ~ devices:", devices);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [assigningDevices, setAssigningDevices] = useState(false);
  const [unassigningDevices, setUnassigningDevices] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Device Modal state
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedNewDevices, setSelectedNewDevices] = useState([]);
  const [devicesLoadingApi, setDevicesLoadingApi] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Initialize cache
  const { getCache, setCache, clearCache } = useAdsCache();

  const [activeTab, setActiveTab] = useState("media");

  // Advertisers and Tags state
  const [advertisers, setAdvertisers] = useState([]);
  const [tags, setTags] = useState([]);

  const CDN_BASE_URL = window.APP_CONFIG?.api3?.baseapi || "";

  const location = useLocation();
  const hasOpenedMediaModalRef = useRef(false);
  const isNewCampaignFlowRef = useRef(false); // Track if this is a new campaign being created
  const [triggerPublishModal, setTriggerPublishModal] = useState(0); // Trigger counter for opening publish modal

  useEffect(() => {
    fetchCampaignDetail();
    fetchDevices();
    fetchAdvertisers();
    fetchAllTags();
    hasOpenedMediaModalRef.current = false; // Reset the flag for new campaign
  }, [id]);

  useEffect(() => {
    if (
      location.state?.openMediaModal &&
      campaign &&
      !hasOpenedMediaModalRef.current
    ) {
      hasOpenedMediaModalRef.current = true;
      isNewCampaignFlowRef.current = true; // Mark this as new campaign flow
      setModalOpen(true);

      setModalSelectedAds([]);
      setMediaType("");
      setAdFormat("");
      setSearchText("");
      setAllAds([]);
      setIsReorderMode(false);
      setDraggedItem(null);
      setCurrentPage(1);
      setTotalPages(1);
      setHasMore(false);

      fetchAds("", "", "", 1);
    }
  }, [location.state, campaign]);

  const getCacheKey = (
    searchText,
    mediaType,
    adFormat,
    pageNum,
    advertiserName = "",
    tags = [],
    fromDate = "",
    toDate = "",
  ) => {
    const tagString = Array.isArray(tags) ? tags.join("_") : "";
    return `ads_${searchText}_${mediaType}_${adFormat}_${advertiserName}_${tagString}_${fromDate}_${toDate}_${pageNum}`;
  };

  // Debounced function for fetching ads - Updated to handle new filter format
  const fetchAds = useCallback(
    debounce(async (pageNum = 1, filters = {}, appendFlag = false) => {
      try {
        setModalLoading(true);

        // Extract filters - handle both old and new format
        const adName = filters?.adName || "";
        const advertiserName = filters?.advertiserName || "";
        const mediaType = filters?.mediaType || "";
        const adFormat = filters?.adFormat || "";
        const tags = filters?.tags || [];
        const minFileSize = filters?.minFileSize;
        const maxFileSize = filters?.maxFileSize;
        const status = filters?.status;
        const durationSeconds = filters?.durationSeconds;
        const fromDate = filters?.fromDate;
        const toDate = filters?.toDate;
        const recentlyUploaded = filters?.recentlyUploaded;

        // Check cache first
        const cacheKey = getCacheKey(
          adName,
          mediaType,
          adFormat,
          pageNum,
          advertiserName,
          tags,
          fromDate,
          toDate,
        );
        const cachedData = getCache(cacheKey);

        if (cachedData && !appendFlag) {
          const { availableAds, totalPages: tp, hasNext } = cachedData;
          setAllAds(availableAds);
          setTotalPages(tp);
          setCurrentPage(pageNum);
          setHasMore(hasNext);
          setModalLoading(false);
          return;
        }

        // Build API params
        const params = {
          pageNumber: pageNum,
          pageSize: filters?.pageSize || 20,
        };

        // Add filter parameters only if they have values
        if (adName) params.adName = adName;
        if (advertiserName) params.advertiserName = advertiserName;
        if (mediaType) params.mediaType = parseInt(mediaType);
        if (adFormat) params.adFormat = parseInt(adFormat);
        if (tags && Array.isArray(tags) && tags.length > 0) params.tags = tags;
        if (minFileSize !== undefined && minFileSize > 0)
          params.minFileSize = minFileSize;
        if (maxFileSize !== undefined && maxFileSize > 0)
          params.maxFileSize = maxFileSize;
        if (status !== undefined && status !== "") params.status = status;
        if (durationSeconds) params.durationSeconds = parseInt(durationSeconds);
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (recentlyUploaded) params.recentlyUploaded = recentlyUploaded;

        console.log("Fetching ads with params:", params);
        if (fromDate || toDate) {
          console.log(
            "🔍 Date filter applied - FromDate:",
            fromDate,
            "ToDate:",
            toDate,
          );
        }

        const res = await AdsService.adsList(params);
        console.log("✅ API Response:", res);
        const assignedAdIds = campaign?.ads?.map((ad) => ad.id) || [];
        const availableAds =
          res?.data?.filter((ad) => !assignedAdIds.includes(ad.id)) || [];

        // Cache the result only for first page
        if (pageNum === 1) {
          setCache(cacheKey, {
            availableAds,
            totalPages: res?.totalPages || 1,
            hasNext: res?.hasNext || false,
          });
        }

        // Update state
        if (appendFlag) {
          setAllAds((prev) => [...prev, ...availableAds]);
        } else {
          setAllAds(availableAds);
        }

        setTotalPages(res?.totalPages || 1);
        setCurrentPage(pageNum);
        setHasMore(res?.hasNext || false);
      } catch (error) {
        console.error("Error fetching media:", error);
        toast.error("Failed to fetch media");
      } finally {
        setModalLoading(false);
      }
    }, 500),
    [campaign?.ads, getCache, setCache],
  );

  // Callback to refresh campaign after media changes
  const handleMediaUpdated = async () => {
    await fetchCampaignDetail();
  };

  async function fetchCampaignDetail() {
    try {
      setLoading(true);
      const response = await CampaignService.getCampaignById(id);
      console.log("🚀 ~ fetchCampaignDetail ~ response:", response)
      if (response) {
        setCampaign(response);
      }
    } catch (error) {
      toast.error("Failed to load campaign details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  // Silent update for ad duration without page refresh
  const handleDurationUpdate = (adId, newDuration) => {
    setCampaign((prev) => ({
      ...prev,
      ads: prev.ads.map((ad) =>
        ad.id === adId
          ? {
              ...ad,
              duration: newDuration,
              durationInSeconds: newDuration,
            }
          : ad,
      ),
    }));
  };

  async function fetchAdvertisers() {
    try {
      // Fetch all advertisers with a large pageSize to get all results
      const response = await AdvertisersService.advertisersList({
        pageSize: 10000, // Fetch all advertisers
      });
      const advertisersList = Array.isArray(response)
        ? response
        : response?.data || [];
      // Extract just the advertiser names for the dropdown
      const names = advertisersList
        .map((adv) => adv.name || adv.advertiserName || adv)
        .filter(Boolean);
      setAdvertisers(names);
    } catch (error) {
      console.error("Failed to fetch advertisers", error);
      setAdvertisers([]);
    }
  }

  async function fetchAllTags() {
    try {
      // Fetch all tags without search filter
      const response = await AdsTagsService.searchTags("");
      const tagsList = Array.isArray(response)
        ? response
        : response?.data || [];
      // Extract just the tag names
      const tagNames = tagsList.map((tag) => tag.name || tag).filter(Boolean);
      setTags(tagNames);
    } catch (error) {
      console.error("Failed to fetch tags", error);
      setTags([]);
    }
  }

  const getMediaUrl = (ad) => {
    if (ad.mediaType === "Video" && ad.thumbnailPath) {
      return `${CDN_BASE_URL}${ad.thumbnailPath}`;
    }
    return `${CDN_BASE_URL}${ad.mediaPath}`;
  };

  const toggleAdSelection = (ad) => {
    const index = selectedAds.findIndex((a) => a.id === ad.id);
    if (index > -1) {
      setSelectedAds(selectedAds.filter((a) => a.id !== ad.id));
    } else {
      setSelectedAds([...selectedAds, ad]);
    }
  };

  const handleUnassign = async () => {
    if (!selectedAds.length) {
      toast.error("Select at least one media to unassign");
      return;
    }

    try {
      setUnassigning(true);
      await CampaignAssignService.unAssignAdsToCampaigns({
        campaignIds: [campaign.id],
        adIds: selectedAds.map((ad) => ad.id),
      });
      toast.success(`✓ Unassigned ${selectedAds.length} media from campaign!`);
      // Update local state directly to avoid page refresh
      const unassignedAdIds = selectedAds.map((ad) => ad.id);
      setCampaign((prev) => {
        const updatedAds = prev.ads.filter(
          (ad) => !unassignedAdIds.includes(ad.id),
        );
        return {
          ...prev,
          ads: updatedAds,
          adsCount: updatedAds.length,
        };
      });
      setSelectedAds([]);
    } catch (error) {
      toast.error("Failed to unassign media");
      console.error(error);
    } finally {
      setUnassigning(false);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
    setModalSelectedAds([]);
    setMediaType("");
    setAdFormat("");
    setSearchText("");
    setAllAds([]);
    setIsReorderMode(false);
    setDraggedItem(null);
    setCurrentPage(1);
    setTotalPages(1);
    setHasMore(false);
    fetchAds(1, {}); // pageNum=1, empty filters
  };


  const handleAddMedia = async (reorderAds = null) => {
    // Case 1: Not in reorder mode - proceed to reorder mode
    if (!isReorderMode) {
      handleProceedToReorder();
      return;
    }

    if (reorderAds && reorderAds.length > 0) {
      try {
        setModalAssigning(true);

        // Step 1: Assign new media if any
        if (modalSelectedAds.length > 0) {
          await CampaignAssignService.assignAdsToCampaigns({
            campaignIds: [id],
            adIds: modalSelectedAds.map((ad) => ad.id),
          });
          toast.success(
            `✓ Assigned ${modalSelectedAds.length} new media to campaign`,
          );
        }

        const adIds = reorderAds.map((ad) => ad.id);
        await CampaignAssignService.reorderCampaignAds(id, adIds);

        toast.success(
          `✓ Reordered ${reorderAds.length} media in campaign successfully!`,
        );

        setCampaign((prev) => ({
          ...prev,
          ads: reorderAds,
          adsCount: reorderAds.length,
        }));

        setModalOpen(false);
        setModalSelectedAds([]);
        setAllAds([]);
        setMediaType("");
        setAdFormat("");
        setSearchText("");
        setIsReorderMode(false);

        setActiveTab("devices");

        if (isNewCampaignFlowRef.current) {
          setTimeout(() => handleOpenDeviceModal(), 300);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to assign and reorder media",
        );
        console.error(error);
      } finally {
        setModalAssigning(false);
      }
      return;
    }

    if (modalSelectedAds.length === 0) {
      toast.error("Please select at least one media");
      return;
    }

    try {
      setModalAssigning(true);
      await CampaignAssignService.assignAdsToCampaigns({
        campaignIds: [id],
        adIds: modalSelectedAds.map((ad) => ad.id),
      });
      toast.success(
        `✓ Successfully assigned ${modalSelectedAds.length} media to campaign!`,
      );
      setCampaign((prev) => {
        const updatedAds = [...(prev.ads || []), ...modalSelectedAds];
        return {
          ...prev,
          ads: updatedAds,
          adsCount: updatedAds.length,
        };
      });
      setModalOpen(false);
      setModalSelectedAds([]);
      setAllAds([]);
      setMediaType("");
      setAdFormat("");
      setSearchText("");
      setIsReorderMode(false);

      // Open device tab
      setActiveTab("devices");

      if (isNewCampaignFlowRef.current) {
        setTimeout(() => handleOpenDeviceModal(), 300);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign media");
      console.error(error);
    } finally {
      setModalAssigning(false);
    }
  };

  const toggleModalAdSelection = (ad) => {
    const index = modalSelectedAds.findIndex((a) => a.id === ad.id);
    if (index > -1) {
      setModalSelectedAds(modalSelectedAds.filter((a) => a.id !== ad.id));
    } else {
      setModalSelectedAds([...modalSelectedAds, ad]);
    }
  };

  const handleProceedToReorder = () => {
    if (modalSelectedAds.length === 0) {
      toast.error("Please select at least one media");
      return;
    }
    setIsReorderMode(true);
  };

  const handleBackToSelection = () => {
    setIsReorderMode(false);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === targetIndex) {
      return;
    }

    const newOrder = [...modalSelectedAds];
    const draggedAd = newOrder[draggedItem];
    newOrder.splice(draggedItem, 1);
    newOrder.splice(targetIndex, 0, draggedAd);
    setModalSelectedAds(newOrder);
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  console.log("Fetching devices for campaign ID:", id);
  // ========== Device Functions ==========
  async function fetchDevices() {
    try {
      setDevicesLoading(true);
      const response = await DeviceHeartbeatService.getDevicesByCampaign(id);

      // Handle paginated response format
      let devicesList = [];
      if (response?.data && Array.isArray(response.data)) {
        devicesList = response.data;
      } else if (Array.isArray(response)) {
        devicesList = response;
      }

      setDevices(devicesList);
    } catch (error) {
      toast.error("Failed to load devices");
      console.error(error);
    } finally {
      setDevicesLoading(false);
    }
  }

  const toggleDeviceSelection = (device) => {
    const index = selectedDevices.findIndex((d) => d.macId === device.macId);
    if (index > -1) {
      setSelectedDevices(
        selectedDevices.filter((d) => d.macId !== device.macId),
      );
    } else {
      setSelectedDevices([...selectedDevices, device]);
    }
  };

  const handleAssignDevices = async () => {
    if (!selectedDevices.length) {
      toast.error("Select at least one device to assign");
      return;
    }

    try {
      setAssigningDevices(true);
      const assignmentData = selectedDevices.map((device, index) => ({
        orderIndex: index,
        macId: device.macId,
        campaignId: id,
      }));

      await DeviceAdsService.bulkAssignCampaigns(assignmentData);
      toast.success(
        `Successfully assigned campaign to ${selectedDevices.length} device(s)!`,
      );
      // Update local state
      setCampaign((prev) => ({
        ...prev,
        devices: [...(prev.devices || []), ...selectedDevices],
      }));
      setSelectedDevices([]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign devices");
      console.error(error);
    } finally {
      setAssigningDevices(false);
    }
  };

  const handleUnassignDevices = async () => {
    if (!selectedDevices.length) {
      toast.error("Select at least one device to unassign");
      return;
    }

    try {
      setUnassigningDevices(true);

      // Create unassignment requests for each device
      const unassignmentPromises = selectedDevices.map((device) =>
        DeviceAdsService.deleteCampaigns(device.macId, [id]),
      );

      await Promise.all(unassignmentPromises);
      toast.success(
        `Unassigned campaign from ${selectedDevices.length} device(s)!`,
      );

      // Get MAC IDs of unassigned devices
      const unassignedMacIds = selectedDevices.map((d) => d.macId);

      // Update campaign - remove unassigned devices
      setCampaign((prev) => ({
        ...prev,
        devices: prev.devices?.filter(
          (device) => !unassignedMacIds.includes(device.macId),
        ),
      }));

      // Update devices list - remove unassigned devices
      setDevices((prev) =>
        prev.filter((device) => !unassignedMacIds.includes(device.macId)),
      );

      // Add unassigned devices back to available list
      const unassignedDevices = selectedDevices;
      setAvailableDevices((prev) =>
        prev.map((org) => ({
          ...org,
          devices: [
            ...org.devices,
            ...unassignedDevices.filter(
              (d) => d.organizationId === org.organizationId,
            ),
          ],
        })),
      );

      setSelectedDevices([]);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to unassign devices",
      );
      console.error(error);
    } finally {
      setUnassigningDevices(false);
    }
  };

  const filteredDevices = devices.filter(
    (d) =>
      d.machineName?.toLowerCase().includes(deviceSearch.toLowerCase()) ||
      d.macId?.toLowerCase().includes(deviceSearch.toLowerCase()),
  );

  const getDeviceImageUrl = () => {
    return kiosk;
  };

  // ========== Device Modal Functions ==========
  const handleOpenDeviceModal = async () => {
    try {
      setDevicesLoadingApi(true);
      const devices = await CloudRestroUsers.getWithDevices({
        onlyWithDevices: true, // Only fetch organizations with devices
      });

      // Handle array response
      let allDevices = Array.isArray(devices) ? devices : devices?.data || [];

      // Get already assigned device MAC IDs
      const assignedMacIds = new Set(
        campaign?.devices?.map((d) => d.macId) || [],
      );

      // Filter out already assigned devices from each organization
      const unassignedDevices = allDevices.map((org) => ({
        ...org,
        devices: org.devices.filter(
          (device) => !assignedMacIds.has(device.macId),
        ),
      }));

      // Only keep organizations that still have unassigned devices
      const orgsWithUnassignedDevices = unassignedDevices.filter(
        (org) => org.devices.length > 0,
      );

      setAvailableDevices(orgsWithUnassignedDevices);
      setDeviceModalOpen(true);
    } catch (error) {
      toast.error("Failed to fetch available devices");
      console.error("Failed to fetch devices", error);
    } finally {
      setDevicesLoadingApi(false);
    }
  };

  const fetchDevicesBySearch = useCallback(
    async (searchText) => {
      try {
        setIsSearching(true);
        const devices = await CloudRestroUsers.getWithDevices({
          onlyWithDevices: true, // Only fetch organizations with devices
          searchText: searchText || "",
        });

        // Handle array response
        let allDevices = Array.isArray(devices) ? devices : devices?.data || [];

        // Get already assigned device MAC IDs
        const assignedMacIds = new Set(
          campaign?.devices?.map((d) => d.macId) || [],
        );

        // Filter out already assigned devices from each organization
        const unassignedDevices = allDevices.map((org) => ({
          ...org,
          devices: org.devices.filter(
            (device) => !assignedMacIds.has(device.macId),
          ),
        }));

        // Only keep organizations that still have unassigned devices
        const orgsWithUnassignedDevices = unassignedDevices.filter(
          (org) => org.devices.length > 0,
        );

        setAvailableDevices(orgsWithUnassignedDevices);
      } catch (error) {
        console.error("Failed to fetch devices by search", error);
      } finally {
        setIsSearching(false);
      }
    },
    [campaign],
  );

  // Debounced search function to avoid too many API calls
  const debouncedSearch = useCallback(
    debounce((searchText) => {
      fetchDevicesBySearch(searchText);
    }, 500),
    [fetchDevicesBySearch],
  );

  const toggleNewDeviceSelection = (device) => {
    setSelectedNewDevices((prev) =>
      prev.some((d) => d.macId === device.macId)
        ? prev.filter((d) => d.macId !== device.macId)
        : [...prev, device],
    );
  };

  const handleAssignNewDevices = async () => {
    if (selectedNewDevices.length === 0) {
      toast.error("Select at least one device to assign");
      return;
    }

    // Get already assigned MAC IDs
    const assignedMacIds = new Set(
      campaign?.devices?.map((d) => d.macId) || [],
    );

    // Filter out any devices that are already assigned
    const devicesToAssign = selectedNewDevices.filter(
      (device) => !assignedMacIds.has(device.macId),
    );

    if (devicesToAssign.length === 0) {
      toast.error("All selected devices are already assigned to this campaign");
      return;
    }

    // Warn if some devices were filtered out
    if (devicesToAssign.length < selectedNewDevices.length) {
      toast(
        `${
          selectedNewDevices.length - devicesToAssign.length
        } device(s) are already assigned and were excluded`,
        {
          icon: "ℹ️",
        },
      );
    }

    try {
      setAssigningDevices(true);
      const assignmentData = devicesToAssign.map((device, index) => ({
        orderIndex: index,
        macId: device.macId,
        campaignId: id,
      }));

      await DeviceAdsService.bulkAssignCampaigns(assignmentData);
      toast.success(
        `Successfully assigned campaign to ${devicesToAssign.length} device(s)!`,
      );

      // Update campaign with newly assigned devices
      setCampaign((prev) => ({
        ...prev,
        devices: [...(prev.devices || []), ...devicesToAssign],
      }));

      // Update devices list with newly assigned devices
      setDevices((prev) => [...prev, ...devicesToAssign]);

      // Remove newly assigned devices from available list
      const assignedMacIdsForFilter = new Set(
        devicesToAssign.map((d) => d.macId),
      );
      setAvailableDevices((prev) =>
        prev.map((org) => ({
          ...org,
          devices: org.devices.filter(
            (device) => !assignedMacIdsForFilter.has(device.macId),
          ),
        })),
      );

      setDeviceModalOpen(false);
      setSelectedNewDevices([]);

      // If this is a new campaign flow, open publish modal automatically
      if (isNewCampaignFlowRef.current) {
        setTimeout(() => {
          toast.success(
            `Campaign setup complete! Opening publish options...`,
            {
              duration: 3000,
            },
          );
          // Trigger publish modal to open
          setTriggerPublishModal(prev => prev + 1);
          // Reset the flow flag
          isNewCampaignFlowRef.current = false;
        }, 500);
      } else {
        // For non-new campaigns, show toast and scroll to button
        setTimeout(() => {
          toast.success(
            `Successfully assigned campaign to ${devicesToAssign.length} device(s)!`,
            {
              duration: 5000,
            },
          );
        }, 500);

        setTimeout(() => {
          document.querySelector('[data-publish-button]')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign devices");
      console.error(error);
    } finally {
      setAssigningDevices(false);
    }
  };

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];
  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "80px" }}
      >
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Campaign Not Found" pageTitle="Campaigns" />
          <Card>
            <CardBody className="text-center py-5">
              <h5 className="text-muted">Campaign details not found</h5>
              <Button
                color="primary"
                onClick={() => navigate("/campaign")}
                className="mt-3"
              >
                Back to List
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <style>{`
        .media-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-radius: 10px;
          overflow: hidden;
        }

        .media-preview {
          overflow: hidden;
          background: #f8f9fa;
          position: relative;
        }

        .media-preview img {
          transition: transform 0.3s ease;
        }

        .media-card:hover .media-preview img {
          transform: scale(1.05);
        }

        .tag-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 20px;
          background: linear-gradient(
            135deg,
            rgba(255, 140, 0, 0.9),
            rgba(255, 165, 0, 0.85)
          );
          color: #fff;
          backdrop-filter: blur(6px);
          box-shadow: 0 3px 8px rgba(255, 140, 0, 0.35);
          letter-spacing: 0.3px;
        }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb
            title={campaign.name}
            pageTitle="Campaigns"
            breadcrumbItems={breadcrumbItems}
          />
        </div>

        <Container fluid>
          <Row>
            <Col lg="12">
              <CampaignInfoCard
                campaign={campaign}
                devices={devices}
                onPublish={fetchCampaignDetail}
                triggerPublishModal={triggerPublishModal}
              />
            </Col>
          </Row>

          {/* Associated Ads */}
          <Row className="mt-2">
            <Col lg="12">
              <Row className="mt-2">
                <Col lg="12">
                  <CampaignTabsCard
                    campaign={campaign}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    selectedAds={selectedAds}
                    setSelectedAds={setSelectedAds}
                    handleUnassign={handleUnassign}
                    unassigning={unassigning}
                    handleOpenModal={handleOpenModal}
                    modalLoading={modalLoading}
                    toggleAdSelection={toggleAdSelection}
                    getMediaUrl={getMediaUrl}
                    onDurationUpdate={handleDurationUpdate}
                    onRefreshCampaign={fetchCampaignDetail}
                    devices={devices}
                    devicesLoading={devicesLoading}
                    filteredDevices={filteredDevices}
                    selectedDevices={selectedDevices}
                    setSelectedDevices={setSelectedDevices}
                    deviceSearch={deviceSearch}
                    setDeviceSearch={setDeviceSearch}
                    toggleDeviceSelection={toggleDeviceSelection}
                    handleAssignDevices={handleAssignDevices}
                    handleUnassignDevices={handleUnassignDevices}
                    assigningDevices={assigningDevices}
                    unassigningDevices={unassigningDevices}
                    getDeviceImageUrl={getDeviceImageUrl}
                    // Device modal props
                    deviceModalOpen={deviceModalOpen}
                    setDeviceModalOpen={setDeviceModalOpen}
                    availableDevices={availableDevices}
                    selectedNewDevices={selectedNewDevices}
                    devicesLoadingApi={devicesLoadingApi}
                    handleOpenDeviceModal={handleOpenDeviceModal}
                    toggleNewDeviceSelection={toggleNewDeviceSelection}
                    handleAssignNewDevices={handleAssignNewDevices}
                    fetchDevicesBySearch={debouncedSearch}
                    setAvailableDevices={setAvailableDevices}
                    isSearching={isSearching}
                    assignedDevices={devices || []}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Add Media Modal */}
          <AddMediaModal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            modalLoading={modalLoading}
            modalAssigning={modalAssigning}
            isReorderMode={isReorderMode}
            modalSelectedAds={modalSelectedAds}
            allAds={allAds}
            hasMore={hasMore}
            currentPage={currentPage}
            totalPages={totalPages}
            searchText={searchText}
            setSearchText={setSearchText}
            fetchAds={fetchAds}
            mediaType={mediaType}
            adFormat={adFormat}
            handleAddMedia={handleAddMedia}
            handleBackToSelection={handleBackToSelection}
            handleProceedToReorder={handleProceedToReorder}
            toggleModalAdSelection={toggleModalAdSelection}
            getMediaUrl={getMediaUrl}
            LazyImage={LazyImage}
            draggedItem={draggedItem}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            handleDragEnd={handleDragEnd}
            advertisers={advertisers}
            tags={tags}
            campaign={campaign}
            onMediaUpdated={handleMediaUpdated}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CampaignDetail;
